<template>
    <div v-if="enabled" class="starsender-google-signin">
        <div
            ref="wrapper"
            class="starsender-google-signin__wrapper"
            :class="{ 'is-busy': busy, 'is-blocked': blocked }">
            <div class="starsender-google-signin__visual" aria-hidden="true">
                <span
                    v-if="busy"
                    class="spinner-border spinner-border-sm starsender-google-signin__spinner"
                    role="status"></span>
                <svg
                    v-else
                    class="starsender-google-signin__icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    width="20"
                    height="20"
                    aria-hidden="true">
                    <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                    <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                    <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                    <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span class="starsender-google-signin__label">{{ label }}</span>
            </div>
            <div
                ref="gisHost"
                class="starsender-google-signin__gis"
                :aria-label="label"
                role="button"
                tabindex="0"></div>
        </div>
    </div>
</template>

<script>
    import { getGoogleClientId, mountGoogleSignInHost } from "@/utils/googleAuth";

    export default {
        name: "GoogleSignInButton",
        props: {
            label: {
                type: String,
                default: "Lanjutkan dengan Google",
            },
            busy: {
                type: Boolean,
                default: false,
            },
            blocked: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                enabled: false,
                resizeObserver: null,
            };
        },
        watch: {
            blocked() {
                this.syncHostPointerEvents();
            },
            busy() {
                this.syncHostPointerEvents();
            },
        },
        async mounted() {
            this.enabled = !!getGoogleClientId();
            if (!this.enabled) {
                return;
            }
            await this.$nextTick();
            await this.mountHost();
            if (typeof ResizeObserver !== "undefined" && this.$refs.wrapper) {
                this.resizeObserver = new ResizeObserver(() => {
                    this.remountHostDebounced();
                });
                this.resizeObserver.observe(this.$refs.wrapper);
            }
        },
        beforeDestroy() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            if (this._remountTimer) {
                clearTimeout(this._remountTimer);
            }
        },
        methods: {
            syncHostPointerEvents() {
                const host = this.$refs.gisHost;
                if (!host) {
                    return;
                }
                const inactive = this.busy || this.blocked;
                host.style.pointerEvents = inactive ? "none" : "auto";
            },
            remountHostDebounced() {
                if (this._remountTimer) {
                    clearTimeout(this._remountTimer);
                }
                this._remountTimer = setTimeout(() => {
                    this.mountHost();
                }, 150);
            },
            async mountHost() {
                const host = this.$refs.gisHost;
                if (!host) {
                    return;
                }
                try {
                    const width = this.$refs.wrapper ? this.$refs.wrapper.offsetWidth : 320;
                    await mountGoogleSignInHost(
                        host,
                        (response) => {
                            this.$emit("credential", response);
                        },
                        { width: Math.max(240, width) }
                    );
                    this.syncHostPointerEvents();
                } catch (e) {
                    console.warn("Google sign-in unavailable", e);
                    this.$emit("error", e);
                }
            },
        },
    };
</script>

<style scoped>
    .starsender-google-signin {
        width: 100%;
    }

    .starsender-google-signin__wrapper {
        position: relative;
        width: 100%;
        min-height: 48px;
    }

    .starsender-google-signin__visual {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 100%;
        min-height: 48px;
        padding: 12px 20px;
        background: #fff;
        border: 1px solid #dadce0;
        border-radius: 4px;
        color: #3c4043;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.25;
        box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
        transition: background 0.15s ease, box-shadow 0.15s ease;
        user-select: none;
    }

    .starsender-google-signin__wrapper:not(.is-busy):not(.is-blocked) .starsender-google-signin__visual {
        cursor: pointer;
    }

    .starsender-google-signin__wrapper:not(.is-busy):not(.is-blocked):hover .starsender-google-signin__visual {
        background: #f8f9fa;
        box-shadow: 0 1px 3px rgba(60, 64, 67, 0.12);
    }

    .starsender-google-signin__wrapper.is-busy .starsender-google-signin__visual,
    .starsender-google-signin__wrapper.is-blocked .starsender-google-signin__visual {
        opacity: 0.72;
    }

    .starsender-google-signin__icon {
        flex-shrink: 0;
        display: block;
    }

    .starsender-google-signin__label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .starsender-google-signin__gis {
        position: absolute;
        inset: 0;
        z-index: 2;
        opacity: 0.011;
        overflow: hidden;
        border-radius: 4px;
    }

    .starsender-google-signin__gis >>> div,
    .starsender-google-signin__gis ::v-deep div {
        width: 100% !important;
        height: 100% !important;
        min-height: 48px !important;
    }

    .starsender-google-signin__gis >>> iframe,
    .starsender-google-signin__gis ::v-deep iframe {
        width: 100% !important;
        height: 48px !important;
        min-height: 48px !important;
        margin: 0 !important;
    }

    .starsender-google-signin__spinner {
        width: 20px;
        height: 20px;
        color: #5f6368;
    }

    @media (max-width: 575px) {
        .starsender-google-signin__visual {
            font-size: 14px;
            padding: 11px 16px;
        }
    }
</style>
